import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { staffApi } from "@/lib/api";
import { subscribe as liveSubscribe, ensureOpen as liveEnsureOpen } from "@/lib/liveSync";
import {
  LayoutGrid, Calendar as CalendarIcon, Users, ShoppingCart, Package, BarChart3,
  Settings, Building2, LogOut, Megaphone, Mail, Tag, Gift, Award, Clock4,
  ChevronLeft, ChevronRight, ChevronDown,
  FolderTree, Repeat, PackageOpen, SlidersHorizontal, Ban, ShieldCheck, ShieldAlert,
  Bell,
} from "lucide-react";
import Logo from "@/components/layout/Logo";
import StartOfDayGate from "@/components/StartOfDayGate";
// Iter-302 — Security logic lives strictly in AuthEngine.jsx. Menu
// visibility, ownerOnly gates and brand-workspace access all resolve
// through the engine's helpers so no surface can accidentally elevate
// a role or un-hide an Owner-only entry.
import {
  hasPermission,
  isOwner,
  isOwnerOrAdmin,
  hasAnyBrandPermission,
  filterMenuByPermissions,
} from "@/lib/AuthEngine";
import { branchThemeStyle, resolveBranchTheme } from "@/lib/branchTheme";

const SIDEBAR_KEY = "cocoon.staff.sidebar.hidden";
const MARKETING_KEY = "cocoon.staff.sidebar.marketing.open";
const GENERAL_KEY = "cocoon.staff.sidebar.general.open";
// Iter-409 — Overview sub-menu open/closed persisted per operator.
const OVERVIEW_KEY = "cocoon.staff.sidebar.overview.open";

/**
 * Two-layer navigation shell. `context` is "brand" or "branch".
 */
export default function StaffShell({ context = "branch", branchName, children }) {
  const { staff, logoutStaff } = useAuth();
  const { bid } = useParams();
  const { pathname } = useLocation();
  const nav = useNavigate();

  const [hidden, setHidden] = useState(() => {
    // Default the drawer to CLOSED on tablet/phone (< lg) so the
    // workspace opens with a clean full-width view. Desktop keeps
    // the operator's last saved preference.
    if (typeof window !== "undefined" && window.innerWidth < 1024) return true;
    return localStorage.getItem(SIDEBAR_KEY) === "1";
  });
  const [marketingOpen, setMarketingOpen] = useState(() => localStorage.getItem(MARKETING_KEY) !== "0");
  const [generalOpen, setGeneralOpen] = useState(() => localStorage.getItem(GENERAL_KEY) !== "0");
  const [overviewOpen, setOverviewOpen] = useState(() => localStorage.getItem(OVERVIEW_KEY) !== "0");

  // Iter-207 — sidebar Reports links are gated on the individual per-user
  // report permissions matrix (the single source of truth). Owner + Admin
  // bypass the check with unconditional access. `summary` is nullable so
  // we don't briefly show / hide the links on first paint — we simply omit
  // the Reports entry until the fetch resolves.
  //
  // Iter-239: re-fetches on the `report_permissions_updated` live-sync
  // event (or on the local window fallback fired from the perms dialog)
  // so newly-granted reports light up the "Reports" link without a
  // manual refresh.
  const [reportSummary, setReportSummary] = useState(null);
  // Iter-411 — brand allow-list fetched so the Cancellation sub-menu
  // can gate on `report_permissions.brand.enabled_keys` independently
  // of the `brand.overview.view` role perm. Owner + Admin bypass with
  // `is_owner` on the summary payload.
  const [brandAllowedKeys, setBrandAllowedKeys] = useState(null);
  const refreshReportSummary = useCallback(() => {
    return Promise.all([
      staffApi.get("/report-permissions/me/summary").then((r) => r.data).catch(() => null),
      staffApi.get("/report-permissions/me", { params: { scope: "brand" } }).then((r) => r.data).catch(() => null),
    ]).then(([summary, brandMe]) => {
      setReportSummary(summary || { is_owner_or_admin: false, brand: { has_any: false }, branch: { has_any: false } });
      setBrandAllowedKeys(new Set((brandMe && brandMe.allowed_keys) || []));
    });
  }, []);
  useEffect(() => {
    let cancelled = false;
    refreshReportSummary().then(() => { if (cancelled) return; });
    return () => { cancelled = true; };
  }, [refreshReportSummary]);

  useEffect(() => {
    if (!staff?.id) return undefined;
    liveEnsureOpen();
    const off = liveSubscribe("report_permissions_updated", (payload) => {
      // Only refetch when the event targets THIS logged-in user — a
      // sidebar rebuild for a peer would be pointless noise.
      if (payload && payload.user_id === staff.id) refreshReportSummary();
    });
    const onLocal = (ev) => {
      const uid = ev?.detail?.user_id;
      if (uid && uid === staff.id) refreshReportSummary();
    };
    window.addEventListener("cocoon:report-permissions-updated", onLocal);
    return () => {
      try { off && off(); } catch { /* noop */ }
      window.removeEventListener("cocoon:report-permissions-updated", onLocal);
    };
  }, [staff?.id, refreshReportSummary]);
  const canSeeBrandReports = isOwnerOrAdmin(staff) || !!reportSummary?.brand?.has_any;
  const canSeeBranchReports = isOwnerOrAdmin(staff) || !!reportSummary?.branch?.has_any;

  useEffect(() => { localStorage.setItem(SIDEBAR_KEY, hidden ? "1" : "0"); }, [hidden]);
  useEffect(() => { localStorage.setItem(MARKETING_KEY, marketingOpen ? "1" : "0"); }, [marketingOpen]);
  useEffect(() => { localStorage.setItem(GENERAL_KEY, generalOpen ? "1" : "0"); }, [generalOpen]);
  useEffect(() => { localStorage.setItem(OVERVIEW_KEY, overviewOpen ? "1" : "0"); }, [overviewOpen]);

  const brandLinks = [
    // Iter-409 — The Overview parent is a collapsible submenu rendered
    // below the plain links (same pattern as "General"). The entry
    // is intentionally omitted from this array so it doesn't paint
    // twice. Child items live in `overviewChildren` further down.
    { to: "/staff/brand/services", label: "Services", icon: CalendarIcon, perm: "brand.services.view" },
    { to: "/staff/brand/series", label: "Series", icon: Repeat, perm: "brand.series.view" },
    { to: "/staff/brand/packages", label: "Packages", icon: PackageOpen, perm: "brand.packages.view" },
    { to: "/staff/brand/products", label: "Products", icon: Package, perm: "brand.products.view" },
    { to: "/staff/brand/customers", label: "Customers", icon: Users, perm: "brand.customers.view" },
    { to: "/staff/brand/users", label: "Staff Logins", icon: Users, perm: "brand.staff_logins.view" },
    { to: "/staff/brand/gift-certificates", label: "Gift Card", icon: Gift, perm: "brand.gift_card.view" },
    { to: "/staff/brand/loyalty", label: "Loyalty Rewards", icon: Award, perm: "brand.loyalty_rewards.view" },
    { to: "/staff/brand/branches", label: "Branches", icon: Building2, ownerOnly: true },
    { to: "/staff/brand/notifications", label: "Automated Notifications", icon: Bell, ownerOnly: true },
    // Iter-207 — sidebar Reports link is gated on the Individual Report
    // Permissions matrix (Owner + Admin bypass). The old `brand.overview.view`
    // check has been retired — individual perms are the sole visibility gate.
    { to: "/staff/brand/reports", label: "Reports", icon: BarChart3, gate: canSeeBrandReports },
  ];
  const visibleBrandLinks = filterMenuByPermissions(brandLinks, staff);

  const branchLinks = bid ? filterMenuByPermissions([
    { to: `/staff/branch/${bid}/calendar`, label: "Calendar", icon: CalendarIcon, perm: "location.booking_calendar.view" },
    { to: `/staff/branch/${bid}/schedule`, label: "Schedule", icon: Clock4, perm: "location.schedule.view" },
    { to: `/staff/branch/${bid}/orders`, label: "Orders / POS", icon: ShoppingCart, perm: "location.orders_pos.view" },
    // Approvals is the void/refund authorization queue — treat the
    // "Delete" tick under Orders / POS as the strict gate. A viewer
    // without delete rights cannot see or open this tray.
    { to: `/staff/branch/${bid}/approvals`, label: "Approvals", icon: ShieldCheck, perm: "location.orders_pos.delete", badgeKey: "approvals" },
    { to: `/staff/branch/${bid}/inventory`, label: "Inventory", icon: Package, perm: "location.inventory.view" },
    { to: `/staff/branch/${bid}/reports`, label: "Reports", icon: BarChart3, gate: canSeeBranchReports },
  ], staff) : [];

  // ---- Pending Approvals Badge (Feb-2026) ---------------------------------
  // Small red pill next to "Approvals" showing the count of `pending` rows
  // for THIS branch. Fetched on mount + refreshed whenever the WS hub fires
  // `pos_approval_updated`. Branch isolation is enforced server-side by
  // scoping `GET /pos-approvals/pending-count?branch_id=<bid>` — no
  // aggregation across the brand.
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const canSeeApprovals = context === "branch" && !!bid && hasPermission(staff, "location.orders_pos.view");
  const refreshApprovalCount = useCallback(async () => {
    if (!canSeeApprovals) return;
    try {
      const r = await staffApi.get("/pos-approvals/pending-count", { params: { branch_id: bid } });
      setPendingApprovals(Number(r?.data?.count) || 0);
    } catch {
      // Silent — the badge is nice-to-have; the tray itself is the
      // source of truth once opened.
    }
  }, [bid, canSeeApprovals]);
  useEffect(() => {
    if (!canSeeApprovals) return undefined;
    refreshApprovalCount();
    liveEnsureOpen();
    // Both the requester (adds a pending row) and the manager
    // (approves/denies) fire `pos_approval_updated`. Each mounted
    // sidebar re-fetches its own branch-scoped count on the event.
    const off = liveSubscribe("pos_approval_updated", (payload) => {
      // Extra branch guard: even though the server broadcasts to
      // every socket, ignore events whose branch_id (when present)
      // isn't ours — cheap safety net that avoids a needless refetch
      // when a peer at another location closes a queue.
      if (payload && payload.branch_id && payload.branch_id !== bid) return;
      refreshApprovalCount();
    });
    return () => { try { off(); } catch { /* noop */ } };
  }, [canSeeApprovals, bid, refreshApprovalCount]);

  const marketingChildren = bid ? filterMenuByPermissions([
    { to: `/staff/branch/${bid}/marketing/emails`, label: "Customer Emails", icon: Mail, perm: "location.customer_emails.view" },
    { to: `/staff/branch/${bid}/marketing/specials`, label: "Specials", icon: Tag, perm: "location.specials.view" },
    { to: "/staff/brand/loyalty", label: "Loyalty Rewards", icon: Award, perm: "brand.loyalty_rewards.view" },
  ], staff) : [];

  // Iter-412 — Overview submenu. Both Brand Overview and Cancellation
  // are now gated SOLELY by the per-user Brand Report Permissions
  // tickboxes. Owner always bypasses; Admin no longer gets automatic
  // access (iter-412 rule 3) — they must tick themselves.
  const canSeeBrandOverview = isOwner(staff)
    || !!(brandAllowedKeys && brandAllowedKeys.has("brand_overview"));
  const canSeeBrandCancellation = isOwner(staff)
    || !!(brandAllowedKeys && brandAllowedKeys.has("cancellation"));
  // Iter-f3 — Service Performance sits alongside Brand Overview and
  // Cancellation under the Overview submenu. Same gating semantics:
  // Owner unconditional; everyone else must tick `service_performance`.
  const canSeeBrandServicePerformance = isOwner(staff)
    || !!(brandAllowedKeys && brandAllowedKeys.has("service_performance"));
  // Iter-f6 — Staff Utilisation. Same gating semantics as the other
  // Overview sub-dashboards: Owner unconditional; everyone else must
  // tick `staff_utilisation` on Brand Report Permissions.
  const canSeeBrandStaffUtilisation = isOwner(staff)
    || !!(brandAllowedKeys && brandAllowedKeys.has("staff_utilisation"));
  const overviewChildren = filterMenuByPermissions([
    { to: "/staff/brand", label: "Brand Overview", icon: LayoutGrid, gate: canSeeBrandOverview, exact: true },
    { to: "/staff/brand/cancellations", label: "Cancellation", icon: Ban, gate: canSeeBrandCancellation },
    { to: "/staff/brand/service-performance", label: "Service Performance", icon: BarChart3, gate: canSeeBrandServicePerformance },
    { to: "/staff/brand/staff-utilisation", label: "Staff Utilisation", icon: Users, gate: canSeeBrandStaffUtilisation },
  ], staff);

  const generalChildren = filterMenuByPermissions([
    { to: "/staff/brand/categories", label: "Categories", icon: FolderTree, perm: "brand.categories.view" },
    { to: "/staff/brand/general/cancellation-reasons", label: "Cancellation Reasons", icon: Ban, perm: "brand.general_settings.view" },
    // Owner-only demolition tool — restricted via AuthEngine's ownerOnly
    // gate so no custom role can accidentally grant it, and the entry
    // is REMOVED from the DOM for non-Owners (Absolute Hiding rule).
    { to: "/staff/brand/general/launch-reset", label: "Launch Reset", icon: ShieldAlert, ownerOnly: true },
  ], staff);

  const links = context === "brand" ? visibleBrandLinks : branchLinks;
  const isMarketingActive = pathname.includes("/marketing/");
  const isGeneralActive = pathname.startsWith("/staff/brand/general/") || pathname.startsWith("/staff/brand/categories");
  // Iter-409 — Overview parent is "active" on either child route.
  const isOverviewActive = pathname === "/staff/brand"
    || pathname.startsWith("/staff/brand/cancellations")
    || pathname.startsWith("/staff/brand/service-performance")
    || pathname.startsWith("/staff/brand/staff-utilisation");

  // The top-bar "Settings" chip is the entry point to Branch Settings and is
  // gated by the parent `branch_settings.view` permission — hiding it when
  // the role has that toggle OFF makes the whole submenu disappear from the
  // UI (children pages further gate themselves per-item).
  const canOpenBranchSettings = hasPermission(staff, "branch_settings.view");
  const settingsHref = context === "branch" && bid && canOpenBranchSettings
    ? `/staff/branch/${bid}/settings`
    : null;

  return (
    <div
      className="min-h-screen bg-[hsl(40_20%_97%)]"
      style={branchThemeStyle(context, branchName)}
      data-branch-theme={context === "branch" ? (resolveBranchTheme(branchName).primary) : "brand-default"}
      data-testid="staff-shell-root"
    >
      {/* Top bar */}
      <header className="border-b border-border bg-white sticky top-0 z-30">
        <div className="h-14 px-4 lg:px-6 flex items-center gap-3">
          <Logo variant="mark" to="/staff/branches" size={36} testid="staff-home-link" />
          <div className="text-xs tracking-overline text-muted-foreground px-3 py-1 rounded-full border border-border hidden sm:block">
            {context === "brand" ? "BRAND WORKSPACE" : `LOCATION · ${branchName || "…"}`}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {hasAnyBrandPermission(staff) && (
              <Button
                variant={context === "brand" ? "default" : "outline"}
                size="sm"
                onClick={() => nav("/staff/brand")}
                data-testid="switch-brand-btn"
                className="gap-2"
              ><Building2 className="w-4 h-4" /> <span className="hidden md:inline">Brand</span></Button>
            )}
            <Button
              variant={context === "branch" && !pathname.endsWith("/settings") ? "default" : "outline"}
              size="sm"
              onClick={() => nav("/staff/branches")}
              data-testid="switch-branches-btn"
              className="gap-2"
            ><Building2 className="w-4 h-4" /> <span className="hidden md:inline">Locations</span></Button>
            {settingsHref && (
              <Button
                variant={pathname === settingsHref || pathname.startsWith(settingsHref + "/") ? "default" : "outline"}
                size="sm"
                onClick={() => nav(settingsHref)}
                data-testid="open-settings-btn"
                className="gap-2"
              ><Settings className="w-4 h-4" /> <span className="hidden md:inline">Settings</span></Button>
            )}
            <div className="ml-3 text-xs text-muted-foreground hidden lg:block">{staff?.full_name} <span className="opacity-60">({staff?.role_detail?.name || staff?.role || "—"})</span></div>
            <Button variant="ghost" size="sm" onClick={() => { logoutStaff(); nav("/staff/login"); }} data-testid="staff-logout-btn"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <div className="flex relative">
        {/* Mobile / tablet backdrop — clicking anywhere off the drawer
            closes the sidebar. Rendered only when the sidebar is
            visible on < lg screens so desktop keeps its permanent rail. */}
        {!hidden && (
          <button
            type="button"
            className="lg:hidden fixed inset-0 z-30 bg-black/40 backdrop-blur-[1px]"
            aria-label="Close menu"
            onClick={() => setHidden(true)}
            data-testid="sidebar-backdrop"
          />
        )}
        {/* Side nav - slides left when hidden. On mobile/tablet renders
            as a fixed-position overlay drawer above the content so it
            never squashes the workspace. */}
        <aside
          className={`shrink-0 border-r border-border bg-white min-h-[calc(100vh-3.5rem)] transition-all duration-300 ease-out overflow-hidden fixed lg:relative top-14 lg:top-0 left-0 z-40 lg:z-auto ${
            hidden ? "w-0 opacity-0 -ml-1" : "w-56 sm:w-60 lg:w-48 opacity-100 shadow-2xl lg:shadow-none"
          }`}
          data-testid="staff-sidebar"
          aria-hidden={hidden}
        >
          <nav className="flex flex-col gap-0.5 p-2 w-56 sm:w-60 lg:w-48" data-testid="sidebar-nav-compact">
            {/* Overview group (brand only) — Iter-409. Renders at the
                top of the brand sidebar with two children: Brand
                Overview (legacy /staff/brand) and Cancellation (new
                brand-level cancellation dashboard). Mirrors the
                General group pattern below for visual consistency. */}
            {context === "brand" && overviewChildren.length > 0 && (
              <div>
                <button
                  type="button"
                  onClick={() => setOverviewOpen((v) => !v)}
                  data-testid="nav-overview-toggle"
                  className={`w-full flex items-center gap-2 px-2 py-1 rounded-md text-sm transition-colors duration-200 ${
                    isOverviewActive ? "bg-primary/10 text-foreground" : "text-foreground hover:bg-muted"
                  }`}
                  aria-expanded={overviewOpen}
                >
                  <LayoutGrid className="w-4 h-4" />
                  <span>Overview</span>
                  <ChevronDown className={`w-4 h-4 ml-auto transition-transform duration-200 ${overviewOpen ? "" : "-rotate-90"}`} />
                </button>
                <div className={`grid transition-all duration-200 ${overviewOpen ? "grid-rows-[1fr] opacity-100 mt-0.5" : "grid-rows-[0fr] opacity-0"}`}>
                  <div className="overflow-hidden">
                    <div className="pl-2 border-l border-border ml-3 flex flex-col gap-0.5">
                      {overviewChildren.map((c) => {
                        const Icon = c.icon;
                        const active = c.exact
                          ? pathname === c.to
                          : pathname.startsWith(c.to);
                        return (
                          <Link
                            key={c.to}
                            to={c.to}
                            data-testid={`nav-overview-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className={`flex items-center gap-2 px-2 py-1 rounded-md text-sm transition-colors duration-200 ${
                              active ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted"
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            <span className="truncate">{c.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {links.map((l) => {
              const Icon = l.icon;
              const active = l.exact ? pathname === l.to : pathname.startsWith(l.to);
              const showApprovalsBadge = l.badgeKey === "approvals" && pendingApprovals > 0;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`flex items-center gap-2 px-2 py-1 rounded-md text-sm transition-colors duration-200 ${
                    active ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="flex-1 truncate">{l.label}</span>
                  {showApprovalsBadge && (
                    <span
                      className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] font-semibold leading-none shadow-sm"
                      data-testid="approvals-pending-badge"
                      aria-label={`${pendingApprovals} pending approval${pendingApprovals === 1 ? "" : "s"}`}
                    >
                      {pendingApprovals > 99 ? "99+" : pendingApprovals}
                    </span>
                  )}
                </Link>
              );
            })}

            {/* General group (brand only) — hidden when every child
                is denied by the caller's role permissions. */}
            {context === "brand" && generalChildren.length > 0 && (
              <div className="mt-0.5">
                <button
                  type="button"
                  onClick={() => setGeneralOpen((v) => !v)}
                  data-testid="nav-general-toggle"
                  className={`w-full flex items-center gap-2 px-2 py-1 rounded-md text-sm transition-colors duration-200 ${
                    isGeneralActive ? "bg-primary/10 text-foreground" : "text-foreground hover:bg-muted"
                  }`}
                  aria-expanded={generalOpen}
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  <span>General</span>
                  <ChevronDown className={`w-4 h-4 ml-auto transition-transform duration-200 ${generalOpen ? "" : "-rotate-90"}`} />
                </button>
                <div className={`grid transition-all duration-200 ${generalOpen ? "grid-rows-[1fr] opacity-100 mt-0.5" : "grid-rows-[0fr] opacity-0"}`}>
                  <div className="overflow-hidden">
                    <div className="pl-2 border-l border-border ml-3 flex flex-col gap-0.5">
                      {generalChildren.map((c) => {
                        const Icon = c.icon;
                        const active = pathname.startsWith(c.to);
                        return (
                          <Link
                            key={c.to}
                            to={c.to}
                            data-testid={`nav-general-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className={`flex items-center gap-2 px-2 py-1 rounded-md text-sm transition-colors duration-200 ${
                              active ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted"
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            <span className="truncate">{c.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Marketing group (branch only) — hidden when every
                child is denied by the caller's role permissions. */}
            {context === "branch" && bid && marketingChildren.length > 0 && (
              <div className="mt-0.5">
                <button
                  type="button"
                  onClick={() => setMarketingOpen((v) => !v)}
                  data-testid="nav-marketing-toggle"
                  className={`w-full flex items-center gap-2 px-2 py-1 rounded-md text-sm transition-colors duration-200 ${
                    isMarketingActive ? "bg-primary/10 text-foreground" : "text-foreground hover:bg-muted"
                  }`}
                  aria-expanded={marketingOpen}
                >
                  <Megaphone className="w-4 h-4" />
                  <span>Marketing</span>
                  <ChevronDown className={`w-4 h-4 ml-auto transition-transform duration-200 ${marketingOpen ? "" : "-rotate-90"}`} />
                </button>
                <div className={`grid transition-all duration-200 ${marketingOpen ? "grid-rows-[1fr] opacity-100 mt-0.5" : "grid-rows-[0fr] opacity-0"}`}>
                  <div className="overflow-hidden">
                    <div className="pl-2 border-l border-border ml-3 flex flex-col gap-0.5">
                      {marketingChildren.map((c) => {
                        const Icon = c.icon;
                        const active = pathname.startsWith(c.to);
                        return (
                          <Link
                            key={c.to}
                            to={c.to}
                            data-testid={`nav-marketing-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className={`flex items-center gap-2 px-2 py-1 rounded-md text-sm transition-colors duration-200 ${
                              active ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted"
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            <span className="truncate">{c.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </nav>
        </aside>

        {/* Sidebar toggle - always visible on the edge. On mobile/tablet
            the drawer is fixed-position so this control sits on top of
            the content regardless of the drawer state. */}
        <button
          type="button"
          onClick={() => setHidden((v) => !v)}
          data-testid="sidebar-toggle-btn"
          aria-label={hidden ? "Show menu" : "Hide menu"}
          className={`fixed top-1/2 -translate-y-1/2 z-50 h-16 w-8 flex items-center justify-center bg-white border border-l-0 border-border rounded-r-md shadow-sm hover:bg-muted transition-all duration-300`}
          style={{ left: hidden ? "0px" : (window.innerWidth >= 1024 ? "188px" : (window.innerWidth >= 640 ? "236px" : "220px")) }}
        >
          {hidden ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>

        {/* Content — pushed by the sidebar only on desktop; on
            tablet/phone the drawer overlays the workspace instead of
            resizing it, so vertical rhythm stays intact. */}
        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 transition-all duration-300">{children}</main>
      </div>
      {context === "branch" && bid && <StartOfDayGate branchId={bid} />}
    </div>
  );
}
